import { initSmartService } from '@pypestream/design-system';
import { init as initI18n, LanguageScopes } from '@pypestream/translations';
import {
  UNKNOWN_ENVIRONMENT,
  initSentryWithLaunchDarklyGate,
  isDevelopment,
  showGitInfo,
} from '@pypestream/utils';
import ReactDOM from 'react-dom/client';

import { AuthProvider } from './global-provider';
import { XstateDevLogger } from './xstate';
import { ldClient } from './xstate/app.xstate';

import { getAnalytics } from './hooks/use-analytics';
import './index.scss';
import { RouterProvider } from './router';

let container: HTMLElement | null = null;

const analytics = getAnalytics();
if (analytics) {
  if (typeof analytics.init === 'function') {
    analytics.init(import.meta.env.FE_POSTHOG_API_KEY, {
      api_host: import.meta.env.FE_POSTHOG_API_HOST,
      persistence: 'localStorage',
      opt_out_capturing_by_default: true,
      disable_session_recording: true,
      session_recording: {
        recordCrossOriginIframes: true,
        maskAllInputs: false,
        maskTextSelector: '[data-private]',
      },
      capture_pageview: true,
      capture_pageleave: true,
      // Pinned explicitly rather than left to the default. posthog-js 1.69 had
      // no such option and behaved as 'always'; from the version this repo now
      // pins, the default is 'identified_only', which stops creating person
      // profiles for anonymous users. Stating it keeps the analytics behaviour
      // identical across the upgrade instead of changing it as a side effect.
      person_profiles: 'always',
    });
  }

  if (typeof analytics.register === 'function') {
    analytics.register({
      'Pypestream Product': 'Manager',
      // Same fallback Sentry uses, so the two tools never disagree about which
      // deploy an event came from — a missing value must look missing in BOTH.
      'Pypestream Environment':
        import.meta.env.ANALYTICS_CAPTURE_ENVIRONMENT || UNKNOWN_ENVIRONMENT,
      'Git Commit': import.meta.env.ANALYTICS_CAPTURE_GIT_COMMIT,
      'Git Tags': import.meta.env.ANALYTICS_CAPTURE_GIT_TAGS
        ? import.meta.env.ANALYTICS_CAPTURE_GIT_TAGS.split(',')
        : null,
      'GitHub PR ID': import.meta.env.ANALYTICS_CAPTURE_GITHUB_PR_ID,
      'GitHub PR URL': import.meta.env.ANALYTICS_CAPTURE_GITHUB_PR_URL,
    });
  }
}

showGitInfo({
  appName: 'Manager',
  commitHash: process.env.GIT_COMMIT_HASH,
  branchName: process.env.GIT_BRANCH_NAME,
  tagName: process.env.GIT_TAG_NAME,
  version: process.env.VITE_APP_VERSION,
});

document.addEventListener('DOMContentLoaded', async function () {
  if (!container) {
    container = document.getElementById('root') as HTMLElement;
    const root = ReactDOM.createRoot(container);

    initSmartService({
      endpoints: {
        gql: import.meta.env.FE_GQL_ENDPOINT,
        wss: import.meta.env.FE_GQL_WSS_ENDPOINT,
        kratos: import.meta.env.FE_KRATOS_ENDPOINT,
      },
      launchDarklyKey: import.meta.env.FE_LAUNCH_DARKLY_API_KEY,
    });

    await initI18n(LanguageScopes.manager);

    const managerSentryDSN = import.meta.env.FE_MANAGER_SENTRY_DSN as string;

    // Which deploys report is decided in LaunchDarkly, not by a list of
    // environment names in the source. `ANALYTICS_CAPTURE_ENVIRONMENT` is still
    // passed, but only as the event's tag.
    //
    // `isDevelopment` stays, and it is not redundant with the flag: local dev
    // builds read the `next` LaunchDarkly environment — the same one the `main`
    // deploy reads — so the flag cannot tell a developer's laptop from the
    // deployed `main`. This is the only thing that can.
    if (!isDevelopment && managerSentryDSN) {
      initSentryWithLaunchDarklyGate(
        managerSentryDSN,
        { environment: import.meta.env.ANALYTICS_CAPTURE_ENVIRONMENT },
        { client: ldClient, flagKey: 'mgr-sentry-enabled' }
      );
    }

    // <React.StrictMode>
    // <React.Suspense
    //   fallback={
    //     <TextTitle>Loading ...</TextTitle>
    //   }
    // >
    root.render(
      <XstateDevLogger>
        <AuthProvider>
          <RouterProvider />
        </AuthProvider>
      </XstateDevLogger>
    );
    // </React.Suspense>
    // </React.StrictMode>
  }
});
